import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { config } from "../../../../config";
import { secureStoreService } from "../secure-store.service";

// Extend the InternalAxiosRequestConfig to include retryCount
export interface RetryConfig extends InternalAxiosRequestConfig {
  retryCount?: number;
}

// Define status codes that should trigger a retry
export const RETRYABLE_STATUS_CODES = new Set([
  408, // Request Timeout
  429, // Too Many Requests
  500, // Internal Server Error
  502, // Bad Gateway
  503, // Service Unavailable
  504, // Gateway Timeout
]);

const apiClient = axios.create({
  baseURL: config.lambda.baseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

export const calculateDelay = (retryCount: number): number => {
  const delay = Math.min(
    config.lambda.retry.initialDelayMs * Math.pow(2, retryCount),
    config.lambda.retry.maxDelayMs
  );
  return delay + Math.random() * 1000; // Add jitter
};

export const shouldRetry = (error: AxiosError): boolean => {
  // If we have a response, check the status code
  if (error.response) {
    return RETRYABLE_STATUS_CODES.has(error.response.status);
  }

  // If we don't have a response, it might be a network error
  // Retry on network errors (ECONNRESET, ETIMEDOUT, etc.)
  return (
    error.code !== undefined &&
    ["ECONNRESET", "ETIMEDOUT", "ECONNABORTED", "ENETUNREACH"].includes(
      error.code
    )
  );
};

const PUBLIC_ROUTES = ["/auth/login", "/auth/register"];

apiClient.interceptors.request.use(async (config) => {
  // Skip token for public routes
  if (PUBLIC_ROUTES.some((route) => config.url?.includes(route))) {
    return config;
  }

  try {
    // Get token using your existing auth service
    const token = await secureStoreService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    console.error("[ApiClient] Failed to get token:", error);
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const reqConfig = error.config as RetryConfig;

    if (!reqConfig) {
      return Promise.reject(error);
    }

    if (typeof reqConfig.retryCount === "undefined") {
      reqConfig.retryCount = 0;
    }

    // Log the failure with status code if available
    const statusCode = error.response?.status;
    console.log(`API call failed. Attempt ${reqConfig.retryCount + 1}`, {
      url: reqConfig.url,
      error: error.message,
      statusCode,
    });

    // Check if we should retry this request
    if (
      shouldRetry(error) &&
      reqConfig.retryCount < config.lambda.retry.maxRetries
    ) {
      reqConfig.retryCount += 1;
      const delay = calculateDelay(reqConfig.retryCount);

      console.log(`Retrying after ${delay}ms... Status code: ${statusCode}`);
      await new Promise((resolve) => setTimeout(resolve, delay));

      return apiClient(reqConfig);
    }

    // If we shouldn't retry or we've exhausted retries, reject with the error
    if (!shouldRetry(error)) {
      console.log(
        `Not retrying request - non-retryable status code: ${statusCode}`
      );
    } else if (reqConfig.retryCount >= config.lambda.retry.maxRetries) {
      console.log(`Max retries (${config.lambda.retry.maxRetries}) exceeded`);
    }

    return Promise.reject(error);
  }
);

export { apiClient };
